import { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cn } from 'components/UI/cn'

type TitleAlign = 'left' | 'center'

/**
 * Shared card chrome for the dashboard widgets: white surface, hairline border,
 * rounded corners and a title/subtitle header with an optional right-aligned
 * `action` slot (typically a <WidgetMenu/> or a tab control).
 *
 * Sized for web apps rather than marketing pages — the TailGrids source used
 * rounded-2xl + p-5/p-6 and lg/xl type; this is tighter (rounded-xl, p-4,
 * base/sm type) so the cards read as dense dashboard tiles. The card fills its
 * container width; the parent grid controls how wide each tile is.
 *
 * Pass `href` to make the whole card click through (e.g. a dashboard tile
 * linking to its full report). Interactive header controls like <WidgetMenu/>
 * must stop propagation so they don't trigger the card's navigation.
 */
export function DashboardCard({
  title,
  subtitle,
  action,
  href,
  children,
  className,
  bodyClassName,
  titleAlign = 'left',
}: {
  title?: ReactNode
  subtitle?: ReactNode
  /** Right-aligned header slot — e.g. <WidgetMenu/> or tabs. */
  action?: ReactNode
  /** When set, the whole card becomes a link to this route. */
  href?: string
  children: ReactNode
  className?: string
  bodyClassName?: string
  /**
   * Title alignment. 'center' centers the title across the full card width and
   * floats the `action` slot to the top-right corner (out of flow), so the title
   * reads as a centered chart caption rather than a left-aligned label. Default
   * 'left'. Used by chart tiles where a left title leaves the top lopsided.
   */
  titleAlign?: TitleAlign
}) {
  const centered = titleAlign === 'center'
  const hasHeader = title != null || subtitle != null || action != null
  const rootClass = cn(
    'flex flex-col rounded-xl border border-line bg-surface p-4 shadow-sm',
    href != null && 'transition-colors hover:border-brand-active',
    className
  )

  const inner = (
    <>
      {hasHeader && (
        // Action stays in flow (never absolutely positioned) so the title cell
        // shrinks and truncates beside it — a long title can't run under the
        // action. A centered title just centers within its (remaining) cell.
        <div className="mb-4 flex items-start justify-between gap-2">
          <div
            className={cn(
              'flex min-w-0 flex-1 flex-col gap-0.5',
              centered && 'items-center text-center'
            )}
          >
            {title != null && (
              <h3 className="text-base font-semibold text-fg">{title}</h3>
            )}
            {subtitle != null && (
              <p className="text-sm text-fg-secondary">{subtitle}</p>
            )}
          </div>
          {action != null && <div className="shrink-0">{action}</div>}
        </div>
      )}
      <div className={cn('flex-1', bodyClassName)}>{children}</div>
    </>
  )

  return href != null ? (
    <Link to={href} className={rootClass}>
      {inner}
    </Link>
  ) : (
    <div className={rootClass}>{inner}</div>
  )
}
